import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../services/api';
import toast from 'react-hot-toast';
import { Helmet } from 'react-helmet';
import { getImageUrl, hideBrokenImage } from '../utils/imageUrl';

const BlogCategory = () => {
  const { slug } = useParams();
  const { user } = useAuth();
  const [category, setCategory] = useState(null);
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const blogsPerPage = 10;

  const fetchCategory = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/categories/${slug}?page=${currentPage}&limit=${blogsPerPage}`);
      setCategory(response.data.category || response.data.data?.category);
      setBlogs(response.data.blogs || response.data.data?.blogs || []);
      setTotalPages(response.data.pagination?.pages || 1);
    } catch (error) {
      toast.error('Category not found');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, slug]);

  useEffect(() => {
    fetchCategory();
  }, [fetchCategory]);

  if (loading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  if (!category) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <Helmet>
          <title>Category Not Found | FBRSTAPP</title>
          <meta name="robots" content="noindex, follow" />
        </Helmet>
        <h1 className="text-2xl font-bold text-gray-800 mb-4">Category Not Found</h1>
        <Link to="/blog" className="text-blue-600 hover:underline">← Back to Blog</Link>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{category.name} - FBR Digital Invoicing Blog | FBRSTAPP</title>
        <meta name="description" content={category.description || `Explore articles, updates and guides on ${category.name} for FBR Digital Invoicing in Pakistan.`} />
        <link rel="canonical" href={`https://fbrstapp.com/blog/category/${slug}`} />
        <meta property="og:title" content={`${category.name} - FBRSTAPP Blog`} />
        <meta property="og:description" content={category.description || `Explore articles on ${category.name}`} />
        <meta property="og:url" content={`https://fbrstapp.com/blog/category/${slug}`} />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Header Navigation */}
        <header className="bg-white shadow-sm border-b sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4">
              <div className="flex items-center">
                <img 
                  src="/fbrlogo1.svg" 
                  alt="FBR Logo" 
                  className="h-10 w-10 mr-3"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <Link to="/" className="text-2xl font-bold text-blue-600 hover:text-blue-700">
                  FBRSTAPP
                </Link>
              </div>
              <nav className="flex items-center space-x-6">
                <Link to="/" className="text-gray-600 hover:text-gray-900 font-medium">Home</Link>
                <Link to="/blog" className="text-gray-600 hover:text-gray-900 font-medium">Blog</Link>
                {user ? (
                  <Link
                    to="/app/dashboard"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                  >
                    Dashboard
                  </Link>
                ) : (
                  <>
                    <Link
                      to="/login"
                      className="text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Sign In
                    </Link>
                    <Link
                      to="/register"
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                    >
                      Get Started
                    </Link>
                  </>
                )}
              </nav>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <div className="bg-blue-600 text-white py-12 px-4">
          <div className="max-w-4xl mx-auto">
            <Link to="/blog" className="hover:underline mb-2 inline-block">← Back to Blog</Link>
            <h1 className="text-4xl font-bold mb-2">{category.name}</h1>
            {category.description && <p className="text-lg">{category.description}</p>}
          </div>
        </div>

        {/* Blog Posts */}
        <div className="max-w-4xl mx-auto px-4 py-12">
          {blogs.length > 0 ? (
            <>
              <div className="space-y-8">
                {blogs.map(blog => {
                  const imageUrl = getImageUrl(blog.featured_image);
                  return (
                  <article key={blog.id} className="bg-white rounded-2xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 border border-gray-100 flex flex-col md:flex-row">
                    <div className="md:w-2/5 flex-shrink-0 bg-gray-100 overflow-hidden relative min-h-[220px] md:min-h-[260px]">
                      <Link to={`/blog/${blog.slug}`} className="block w-full h-full">
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={blog.title}
                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-500 min-h-[220px]"
                            onError={hideBrokenImage}
                          />
                        ) : (
                          <div className="w-full h-full min-h-[220px] bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center p-6 text-white text-center font-bold text-lg">
                            FBRSTAPP
                          </div>
                        )}
                      </Link>
                    </div>
                    <div className="p-6 md:p-8 flex-1 flex flex-col justify-between">
                      <div>
                        <Link
                          to={`/blog/${blog.slug}`}
                          className="block text-xl md:text-2xl font-bold text-gray-900 hover:text-blue-600 transition leading-snug mb-3"
                        >
                          {blog.title}
                        </Link>
                        <div className="flex flex-wrap items-center gap-3 text-gray-500 text-xs sm:text-sm mb-4">
                          <span>By {blog.author_name}</span>
                          <span>•</span>
                          <span>{new Date(blog.publish_date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                          <span>•</span>
                          <span>{blog.views} views</span>
                        </div>
                        <p className="text-gray-600 text-sm leading-relaxed line-clamp-3 mb-4">
                          {blog.excerpt || (blog.content ? blog.content.replace(/<[^>]*>?/gm, '').substring(0, 180) : '')}...
                        </p>
                      </div>
                      <div>
                        <Link
                          to={`/blog/${blog.slug}`}
                          className="inline-flex items-center text-blue-600 hover:text-blue-800 font-bold text-sm group"
                        >
                          Read Full Article <span className="ml-1 group-hover:translate-x-1 transition-transform">→</span>
                        </Link>
                      </div>
                    </div>
                  </article>
                  );
                })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center gap-2 mt-8">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-4 py-2 rounded ${
                        currentPage === page
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 hover:bg-gray-300'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 text-gray-600">
              No blog posts in this category yet.
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default BlogCategory;
